import { Injectable, inject, signal } from '@angular/core';
import { CalendarService } from '../../../services/calendar.service';
import { ShiveRenderService } from '../../../../../core/services/rendering/shive-render.service';
import { MonthCalendarModel } from '../../../../../core/models/calendar/month-calendar.model';
import { combineMaps } from '../../../../../shared/functions/map-functions';
import { IFixIntervalViewType } from '../types/time-block-types';
import {
  FixIntervalTimeBlockHeightPx,
  HorizontalTimeBlockGapPx,
  MonthViewTimeBlockContainer,
  ShowMoreButtonCSSSelector,
} from '../../../util/calendar-style.constants';
import { ITimeBlockComponentItem } from '../types/time-block-component-item-types';
import { TimeBlockElementSelectorService } from '../rendering/time-block-element-selector.service';
import { ShivePopupService } from '../../../../../core/services/controls/shive-popup.service';
import { OverflowingTimeBlockMonthViewDialogComponent } from '../../../dialogs/overflowing-time-block-month-view-dialog/overflowing-time-block-month-view-dialog.component';

@Injectable()
export class TimeBlockMonthViewContainerService {
  public hiddenTimeBlockCountPerBucket = signal<number[]>([]);

  // DI
  private readonly calendarService = inject(CalendarService);
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly shivePopupService = inject(ShivePopupService);

  public toggleOverflowingTimeBlocks(): void {
    const calendarModel = this.calendarService.model as MonthCalendarModel;
    const fixIntervalTimeBlockMap = calendarModel.fixIntervalCalendarModel.timeBlocks;
    const variableIntervalTimeBlockMap = calendarModel.timeBlocks;
    const mergedTimeBlocks = combineMaps(fixIntervalTimeBlockMap, variableIntervalTimeBlockMap);

    let bucketIndex = 0;
    this.hiddenTimeBlockCountPerBucket.set([]);
    mergedTimeBlocks.forEach((timeBlocks) => {
      const currentIndex = bucketIndex; // this matches your template's "index"

      // helper to set the signal value at currentIndex
      const setHiddenCount = (value: number) => {
        this.hiddenTimeBlockCountPerBucket.update((arr) => {
          const copy = [...arr];
          copy[currentIndex] = value;
          return copy;
        });
      };

      // Move to next bucket if there are no blocks here
      if (!timeBlocks || timeBlocks.length === 0) {
        setHiddenCount(0);
        bucketIndex++;
        return;
      }

      const maxParallelIndex = this.getMaxParallelIndex(timeBlocks) + 1; // keep the "+ 1" logic from original code

      const bucketContainer =
        this.calendarService.model.timeBlockHtmlContainers[bucketIndex].nativeElement;
      bucketIndex++;

      const timeBlockContainer = bucketContainer.querySelector(`.${MonthViewTimeBlockContainer}`);

      if (!timeBlockContainer) {
        setHiddenCount(0);
        return;
      }

      const containerHeight =
        timeBlockContainer.getBoundingClientRect().height + HorizontalTimeBlockGapPx;
      const accumulatedTimeBlockHeight =
        maxParallelIndex * (FixIntervalTimeBlockHeightPx + HorizontalTimeBlockGapPx);

      const hasOverflow = containerHeight < accumulatedTimeBlockHeight;
      const visibleTimeBlockCount = Math.floor(
        containerHeight / (FixIntervalTimeBlockHeightPx + HorizontalTimeBlockGapPx),
      );
      this.showHiddenTimeBlocks(visibleTimeBlockCount + 1, timeBlocks);

      // Store hidden time block count.
      const hiddenCount = Math.max(0, maxParallelIndex - visibleTimeBlockCount);
      setHiddenCount(hiddenCount);

      if (hasOverflow) {
        this.displayHiddenTimeBlockCountButton(bucketContainer);
        this.hideOverflowingTimeBlocks(visibleTimeBlockCount, timeBlocks);
      } else {
        this.hideShowAllButton(bucketContainer);
      }
    });
  }

  public showAllCellTimeBlocks($event, bucket: HTMLDivElement): void {
    $event.stopPropagation();
    this.shivePopupService.open({
      content: OverflowingTimeBlockMonthViewDialogComponent,
      anchor: bucket,
      popupClass: 'popup',
    });
  }

  private getMaxParallelIndex(timeBlocks: any[]): number {
    return timeBlocks.reduce((max, block) => {
      const parallelIndex = (block.timeBlockModel.timeBlockViewType as IFixIntervalViewType)
        .parallelIndex;
      return Math.max(max, parallelIndex);
    }, 0);
  }

  private hideOverflowingTimeBlocks(
    visibleTimeBlockCount: number,
    timeBlocks: ITimeBlockComponentItem[],
  ): void {
    const hiddenTimeBlocks = timeBlocks.filter(
      (timeBlock) =>
        (timeBlock.timeBlockModel.timeBlockViewType as IFixIntervalViewType).parallelIndex >=
        visibleTimeBlockCount,
    );

    hiddenTimeBlocks.forEach((timeBlock) => {
      const timeBlockHTMLWRapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(
        timeBlock,
        true,
      );
      // timeBlockHTMLWrapper is null for fix interval time block parts that are not the head part.
      if (timeBlockHTMLWRapper) {
        this.shiveRenderService.addClass(timeBlockHTMLWRapper, '!k-display-none');
      }
    });
  }

  private showHiddenTimeBlocks(
    visibleTimeBlockCount: number,
    timeBlocks: ITimeBlockComponentItem[],
  ) {
    const visibleTimeBlocks = timeBlocks.filter(
      (timeBlock) =>
        (timeBlock.timeBlockModel.timeBlockViewType as IFixIntervalViewType).parallelIndex <
        visibleTimeBlockCount,
    );

    visibleTimeBlocks.forEach((timeBlock) => {
      const timeBlockHTMLWRapper = TimeBlockElementSelectorService.getTimeBlockHTMLWrapper(
        timeBlock,
        true,
      );
      if (timeBlockHTMLWRapper) {
        this.shiveRenderService.removeClass(timeBlockHTMLWRapper, '!k-display-none');
      }
    });
  }

  private displayHiddenTimeBlockCountButton(bucketContainer: HTMLTableCellElement) {
    const showMoreButton = bucketContainer.querySelector(`.${ShowMoreButtonCSSSelector}`);
    this.shiveRenderService.removeClass(showMoreButton, 'k-d-none');
    this.shiveRenderService.addClass(showMoreButton, 'k-d-block');
  }

  private hideShowAllButton(bucketContainer: HTMLTableCellElement) {
    const showMoreButton = bucketContainer.querySelector(`.${ShowMoreButtonCSSSelector}`);
    this.shiveRenderService.removeClass(showMoreButton, 'k-d-block');
    this.shiveRenderService.addClass(showMoreButton, 'k-d-none');
  }
}
